import { getAuth } from 'firebase/auth';
import { collection, deleteDoc, doc, getDoc, getFirestore, onSnapshot, setDoc } from 'firebase/firestore';
import { ArrowDown, ArrowUp, Book, Download, Edit, FileText, Play, Plus, Trash, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { saveAs } from 'file-saver';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"; // Import dialog components  
import { useToast } from '../../components/ui/use-toast';
import { app } from "../../firebase";
import { Label } from '../ui/label';
import CreateModuleDialog from './CreateModuleDialog';
import SubjectSearchDropdown from './SubjectSearchDropdown';
import { Subject } from './types';

interface ModulesTabProps {
  courseId: string;
  subjects: Subject[];
  onSubjectsChange: (subjects: Subject[]) => void;
}


// Mock data for materials and videos  
const moduleMaterials: Record<string, any[]> = {
  '1': [
    { id: 'm1', name: 'HTML Basics.pdf', filePath: '/materials/html-basics.pdf', batchName: 'JFS-8' },
    { id: 'm2', name: 'HTML5 New Features.pdf', filePath: '/materials/html5-features.pdf', batchName: 'PFS-12' },
  ],
  '2': [
    { id: 'm3', name: 'CSS Selectors Guide.pdf', filePath: '/materials/css-selectors.pdf', batchName: 'JFS-8' },
  ],
  '4': [
    { id: 'm4', name: 'React Components Explained.pdf', filePath: '/materials/react-components.pdf', batchName: 'PFS-11' },
  ],
};

const moduleVideos: Record<string, any[]> = {
  '1': [
    { id: 'v1', title: 'Introduction to HTML', url: 'https://youtu.be/oMZNw', batchName: 'JFS-8' },
    { id: 'v2', title: 'HTML Document Structure', url: 'https://youtu.be/8Y-ziEr', batchName: 'PFS-11' },
  ],
  '2': [
    { id: 'v3', title: 'CSS Basics', url: 'https://youtu.be/css-basic', batchName: 'JFS-8' },
    { id: 'v4', title: 'CSS Box Model', url: 'https://youtu.be/box-model', batchName: 'PFS-12' },
  ],
};

const ModulesTab = ({ courseId, batches, subjects, isReadonly, allSubjects, onSubjectsChange }: any) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [expandedSubjects, setExpandedSubjects] = useState<Record<string, boolean>>({});
  const [newSubjectName, setNewSubjectName] = useState(null);
  const [activeSubject, setActiveSubject] = useState<string | null>(
    subjects?.length > 0 ? subjects[0].subjectId : null
  );
  const [isCreateModuleOpen, setIsCreateModuleOpen] = useState(false);

  const db = getFirestore(app);
  const auth = getAuth(app);



  const [showAddSubjectDialog, setShowAddSubjectDialog] = useState(false);
  const [showAddModuleDialog, setShowAddModuleDialog] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [newModuleName, setNewModuleName] = useState('');
  // State for the add module dialog  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newSubjectData, setNewSubjectData] = useState({ subjectName: '' });
  const [allsubjects, setSubjects] = useState<any[]>([]); // State to hold fetched subjects  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedModuleToDelete, setSelectedModuleToDelete] = useState('');
  const [subjectIdOfDeleteModuleSlected, setSubjectIdOfDeleteModuleSlected] = useState('');

  const handleEditModule = (moduleId: string) => {
    localStorage.setItem("lastSubjectSelected", JSON.stringify({ activeSubject: activeSubject }));
    localStorage.setItem("isModulesActiveBack", JSON.stringify({ isModulesActiveBack: true }))
    navigate(`/modules/edit/${moduleId}?courseId=${courseId}&subjectId=${activeSubject}`);
  };

  const handleAddModule = () => {
    setShowAddModuleDialog(true)
  };


  // Example useEffect to fetch modules based on subjects  
  useEffect(() => {
    const fetchModuleData = async () => {
      const updatedSubjects = await Promise.all(subjects?.map(async (subject) => {
        // Check if the subject has moduleIds  
        if (subject?.moduleIds && subject?.moduleIds.length > 0) {
          const modulePromises = subject?.moduleIds?.map(async (id) => {
            const moduleDocRef = doc(db, 'modules', id); // Reference to the specific module  
            const moduleDoc = await getDoc(moduleDocRef); // Fetch the module document  

            if (moduleDoc.exists()) {
              return {
                id: moduleDoc.id,
                ...moduleDoc.data(), // Spread the module data  
              };
            }
            return null; // In case the document doesn't exist  
          });

          // Wait for all module data to be resolved  
          const modules = await Promise.all(modulePromises);
          return {
            ...subject,
            modules: modules.filter(module => module !== null), // Filter out nulls from non-existing modules  
          };
        }
        return subject; // Return the subject as-is if there are no moduleIds  
      }));

      setSubjects(updatedSubjects);
      setActiveSubject(updatedSubjects[0]?.subjectId);
    };

    fetchModuleData();
    checkActiveSubject();
    clearLocalStorageRetain();
  }, [subjects]); // Run when subjects change  

  const checkActiveSubject = () => {
    const activeSubject = JSON.parse(localStorage.getItem("lastSubjectSelected"));
    if (activeSubject?.activeSubject) {
      setActiveSubject(activeSubject?.activeSubject);
    }
  }

  const clearLocalStorageRetain = () => {
    localStorage.removeItem("lastSubjectSelected");
    localStorage.removeItem("isModulesActiveBack");
  }
  const handleSaveModule = async (moduleData: { moduleName: string; moduleDescription: string }) => {
    if (!activeSubject) return;



    // Create the module data to save it  
    const body = {
      moduleName: moduleData.moduleName,
      moduleDescription: moduleData.moduleDescription,
      isCompleted: false,
      moduleTopics: {},
      subjectId: activeSubject, // Reference to the subject the module belongs to  
    };


    try {
      // Save the module data in the "modules" collection  
      const moduleRef = doc(collection(db, "modules")); // Create a new document reference  
      await setDoc(moduleRef, { id: moduleRef.id, ...body }); // Save it with an auto-generated ID  

      // Update subjects to include the new module  
      const updatedSubjects = subjects?.map(subject => {
        if (subject?.subjectId === activeSubject) {
          return {
            ...subject,
            moduleIds: [
              ...subject?.moduleIds,
              moduleRef.id
            ]
          };
        }
        return subject;
      });

      onSubjectsChange(updatedSubjects); // Update the subjects state with the new module  
      setIsCreateModuleOpen(false); // Close the module creation dialog  

      // Show success toast notification  
      toast({
        title: "Module created",
        description: `${moduleData.moduleName} has been added successfully.`,
        variant: "default"
      });


      const courseRef = doc(db, "courses", courseId);
      const courseSnap = await getDoc(courseRef);

      if (courseSnap.exists()) {
        const courseData = courseSnap.data();
        courseData.subjects?.filter(x => x.subjectId == activeSubject)[0].moduleIds.push(moduleRef.id);
        // Do something with courseData
        await setDoc(courseRef, courseData);
      } else {
        // docSnap.data() will be undefined in this case
        console.log("No such course found!");
      }
      navigate(`/modules/edit/${moduleRef.id}/?courseId=${courseId}&subjectId=${activeSubject}`);

    } catch (error) {
      // Handle errors gracefully  
      console.error("Error adding module:", error);
      toast({
        title: "Error creating module",
        description: "There was a problem creating the module. Please try again.",
        variant: "destructive"
      });
    }
  };



  const handleAddSubjectPopup = () => {
    setShowAddSubjectDialog(true);
  }



  const handleSaveNewSubject = () => {
    const newSubject = {
      id: `${new Date().getTime()}`, // Generate a simple unique ID  
      subjectName: newSubjectData.subjectName,
      modules: [], // Initialize with no modules  
    };

    onSubjectsChange([...subjects, newSubject]); // Add the new subject to the subjects  
    setEditDialogOpen(false); // Close the dialog  
    setNewSubjectData({ subjectName: '' }); // Reset form data  
    toast({
      title: 'Subject Added',
      description: 'The new subject has been added successfully.',
    });
  };

  const handleFormChange = (field: string, value: string) => {
    setNewSubjectData((prev) => ({ ...prev, [field]: value })); // Update the corresponding field  
  };

  const getActiveSubject = () => {
    return allsubjects?.find(subject => subject.subjectId === activeSubject);
  };

  const moveModuleUp = (subjectId: string, index: number) => {
    if (index === 0) return;

    const updatedSubjects = subjects?.map(subject => {
      if (subject.subjectId === subjectId) {
        const updatedModules = [...subject.moduleIds];
        [updatedModules[index], updatedModules[index - 1]] = [updatedModules[index - 1], updatedModules[index]];
        return { ...subject, moduleIds: updatedModules };
      }
      return subject;
    }) ?? [];

    onSubjectsChange(updatedSubjects);
  };

  const moveModuleDown = (subjectId: string, index: number) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    if (!subject || index === subject?.moduleIds?.length - 1) return;

    const updatedSubjects = subjects?.map(subject => {
      if (subject.id === subjectId) {
        const updatedModules = [...subject?.moduleIds];
        [updatedModules[index], updatedModules[index + 1]] = [updatedModules[index + 1], updatedModules[index]];
        return { ...subject, moduleIds: updatedModules };
      }
      return subject;
    }) ?? [];

    onSubjectsChange(updatedSubjects);
  };

  const handleDeleteConfirmModule = (subjectId: string, moduleId: string) => {
    setSubjectIdOfDeleteModuleSlected(subjectId);
    setSelectedModuleToDelete(moduleId);
    setIsDeleteDialogOpen(true);
  }

  const getBatchName = (batchId) => {
    return batches?.find(x => x.id == batchId)?.batchName;
  }
  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    handleDeleteModule(subjectIdOfDeleteModuleSlected, selectedModuleToDelete);
  }

  const handleDeleteModule = async (subjectId: string, moduleId: string) => {
    // Optimistically update the local subjects state to remove the module immediately  
    const updatedSubjects = allsubjects?.map(subject => {
      if (subject.subjectId === subjectId) {
        return {
          ...subject,
          moduleIds: subject?.moduleIds?.filter(module => module !== moduleId)
        };
      }
      return subject;
    });

    // Update the state immediately for UI feedback  
    onSubjectsChange(updatedSubjects);

    try {
      // Now, delete the module from Firestore  
      await deleteDoc(doc(db, "modules", moduleId));
      toast({
        title: "Module deleted",
        description: "The module has been removed successfully.",
      });
    } catch (error) {
      // If there was an error during deletion revert the subjects state back  
      onSubjectsChange(subjects); // Optionally, you may want to refetch the data instead  
      toast({
        title: "Failed to Delete",
        description: `Failed to delete the module`,
        variant: 'destructive'
      });
    }
  };

  // Fetch subjects from Firestore on component mount  
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "allSubjects"), (querySnapshot) => {
      const result = querySnapshot.docs?.map((doc) => ({
        id: doc.id,  // Get the document ID  
        ...doc.data(),
        // Spread the document data  
      }))?.map((x: any) => {
        return {
          ...x,
          subjectName: x.className
        }
      });
      setSubjects(result); // Update state with fetched subjects  
      if (result.length > 0) {
        setActiveSubject(result[0].id); // Set the first subject as active if subjects exist  
      }
    }, (error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" }); // Handle errors  
    });

    return () => {
      unsubscribe(); // Clean up listener on component unmount  
    };
  }, []);


  const handleAddSubject = () => {
    if (!newSubjectName?.trim()) {
      toast({
        title: "Error",
        description: "Please enter a subject name",
        variant: "destructive"
      });
      return;
    }

    if (subjects.some(subject => subject?.subjectName?.toLowerCase() === newSubjectName?.toLowerCase())) {
      toast({
        title: "Error",
        description: "This subject already exists",
        variant: "destructive"
      });
      return;
    }
    const newSubject: any = allSubjects?.find((subject: any) =>
      subject?.subjectName?.toLowerCase() == newSubjectName?.toLowerCase());

    const newSubjectObj: any = {
      subjectId: newSubject?.id,
      subjectName: newSubject?.subjectName,
      modules: []
    };

    onSubjectsChange([...subjects, newSubjectObj]);
    setNewSubjectName('');
    setShowAddSubjectDialog(false);

    setExpandedSubjects(prev => ({
      ...prev,
      [newSubject.id]: true
    }));

    toast({
      title: "Subject Added",
      description: `${newSubjectName} has been added successfully.`,
    });
  };



  const handleDownload = async (link, fileName) => {
    try {
      const response = await axios.get(link, {
        responseType: 'blob', // Ensure the response is treated as a blob  
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            console.log(`Download progress: ${percentDone}%`);
          }
        },
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, link.split('/').pop());
    } catch (error) {
      console.error('Download error:', error.response ? error.response.data : error.message);
    }
  };
  ;


  const downloadFileByUrlandName = async (fileUrl: any, fileName: string) => {
    if (!fileUrl) {
      return;
    }
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link: any = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName + ".pdf"); // Provide a name for the file  
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      alert(error)
    }
  };

  const getBatchNameForDisplay = (batchId: string) => {
    const batch = batches.find(b => b.id === batchId);
    return batch ? batch.batchName : batchId;
  };

  const getCompletedBatchesCount = (bacthes: any, moduleId: string) => {
    return batches?.filter(b => b.isCompleted).length || 0;
  };

  const getTotalBatchesCount = (bacthes: any) => {
    return batches?.length || 0;
  };

  const getActiveSubjectModulesLength = () => {
    const activeModulesCount = getActiveSubject()?.modules?.length ?? 0;
    return activeModulesCount;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {
          !isReadonly && <Button
            type="button"
            onClick={handleAddSubjectPopup}  // Open the dialog to add a new module  
            className="mb-2"
          >
            <Plus className="mr-1 h-4 w-4" /> {/* Plus icon */}
            <Book className="mr-1 h-4 w-4" /> {/* Module icon */}
          </Button>
        }
        <div className="flex flex-wrap gap-2">
          {subjects?.map((subject, i) => (
            <Button
              type="button"  // Ensure this is set to prevent default submit behavior  
              key={subject.subjectId + Number(i + 1) + Date.now().toString()+i}
              variant={activeSubject === subject.subjectId ? "default" : "outline"}
              onClick={(e) => {
                e.preventDefault(); // Prevent default action  
                e.stopPropagation(); // Prevent event from bubbling up  
                setActiveSubject(subject.subjectId); // Change the active subject  
              }}
              className="mb-2"
            >
              {subject?.subjectName}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-xl font-semibold">
            {activeSubject ? getActiveSubject()?.subjectName : 'No Subject Selected'} Modules
          </CardTitle>
          {
            !isReadonly &&
            <Button onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddModule();
            }} disabled={!activeSubject}>
              <Plus className="mr-2 h-4 w-4" />
              Add Module
            </Button>
          }

        </CardHeader>
        <CardContent>
          {!activeSubject ? (
            <div className="text-center py-12 border rounded-md bg-gray-50">
              <p className="text-gray-500 mb-4">Please select a subject to view its modules.</p>
            </div>
          ) : getActiveSubjectModulesLength() <= 0 ? (
            <div className="text-center py-12 border rounded-md bg-gray-50">
              <p className="text-gray-500 mb-4">No modules have been added to this subject yet.</p>
              <Button onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddModule();
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Add First Module
              </Button>
            </div>
          ) : (
            <Accordion type="multiple" className="space-y-4">
              {getActiveSubject()?.modules?.map((module, index) => (
                <AccordionItem
                  key={module.id + index + 1 + Date.now().toString()+index}
                  value={module.id + index + 1 + Date.now().toString()+index}
                  className="border rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex items-start justify-between p-4 py-1 border-b">
                    <div className="space-y-1">
                      <AccordionTrigger className="flex flex-col hover:no-underline">
                        <div className="text-left flex items-center">
                          <Book className="h-5 w-5 mr-2 text-blue-500" /> {/* Add icon for module */}
                          <div>
                            <h3 className="font-medium text-lg">{"(Module"+" "+(index+1)+" )"+" "}{module?.moduleName}</h3>
                            {getCompletedBatchesCount(module?.batches, module?.id) > 0 && (
                              <Badge variant="success" className="ml-2 text-sm">
                                {getCompletedBatchesCount(module?.batches, module?.id)}/{getTotalBatchesCount(module?.batches)} Completed
                              </Badge>
                            )}

                            <p className="text-sm text-gray-500 line-clamp-2 mt-1">{module?.moduleDescription}</p>
                          </div>

                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {module?.batches?.map((batch, idx) => (
                            <Badge
                              key={batch?.batchId+idx+1 || idx+1} // Use batchId alone or idx for a safe fallback  
                              variant={batch.isCompleted ? "success" : "outline"}
                              className="text-xs flext-start"
                            >
                              {getBatchNameForDisplay(batch.batchId)}
                            </Badge>
                          ))}
                        </div>
                      </AccordionTrigger>
                    </div>
                    {
                      !isReadonly && <div className="flex gap-2 ml-4 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            moveModuleUp(activeSubject!, index);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={index === getActiveSubject()?.modules.length! - 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            moveModuleDown(activeSubject!, index);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleEditModule(module.id);
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDeleteConfirmModule(activeSubject!, module.id);
                          }}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive/90"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    }

                  </div>

                  <AccordionContent className="p-0">
                    <div className="px-4 py-5 space-y-6">
                      {/* Module Topics */}
                      <div>
                        <h4 className="font-medium text-sm text-gray-500 uppercase mb-3">Topics</h4>
                        {typeof module?.moduleTopics === 'object' && !Array.isArray(module?.moduleTopics) ? (
                          // Case when moduleTopics is an object  
                          <ul className="mt-2 p-2">
                            {module?.moduleTopics?.topic?.map((topic, index) => (
                              <li key={index+1}>{topic}</li>
                            ))}
                          </ul>
                        ) : Array.isArray(module?.moduleTopics) ? (
                          // Case when moduleTopics is an array  
                          <ul className="mt-2 p-2">
                            {module?.moduleTopics.map((item, index) => {
                              const topicKey = Object.keys(item.topic)[0]; // Get the first key from the topic object  
                              const subTopics = item.topic[topicKey]; // Get the array of subTopics  

                              return (
                                <li key={index+1} className="mt-2 p-2">
                                  <strong>{topicKey}:</strong>
                                  <ul className="mt-2">
                                    {subTopics.map((subTopic, subIndex) => (
                                      <li key={subIndex+1}>{subTopic}</li>
                                    ))}
                                  </ul>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p>Invalid format for moduleTopics.</p> // Handle invalid format  
                        )}
                      </div>

                      {/* Module Materials */}
                      {module?.materials && module?.materials.length > 0 && (
                        <div className="border-t pt-5">
                          <h4 className="font-medium text-sm text-gray-500 uppercase mb-3">Materials</h4>
                          <div className="space-y-3">
                            {module?.materials?.map((material, index) => (
                              <div
                                key={material.id + Number(Date.now().toString())+1}
                                className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-center">
                                  <FileText className="h-5 w-5 text-blue-500 mr-3" />
                                  <div>
                                    <p className="font-medium">{material?.fileName ?? module?.moduleName}</p>
                                    {/* {material.batchName && ( */}
                                    {/* <Badge variant="outline" className="mt-1 text-xs bg-blue-50 text-blue-700 hover:bg-blue-50">
                                    
                                      </Badge> */}
                                    {/* )} */}
                                  </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => {
                                  handleDownload(material?.link, material?.fileName ?
                                    material?.fileName : module?.moduleName)
                                }} className="text-gray-500 hover:text-gray-700">
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Module Videos */}
                      {module?.recordings && module?.recordings.length > 0 && (
                        <div className="border-t pt-5">
                          <h4 className="font-medium text-sm text-gray-500 uppercase mb-3">Video Recordings</h4>
                          <div className="space-y-3">
                            {module?.recordings?.map((video, i) => (
                              <div
                                key={video.id + Date.now().toString()+1}
                                className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-center">
                                  <div className="bg-red-100 p-2 rounded-md mr-3">
                                    <Play className="h-4 w-4 text-red-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{'Recorded Video' + " " + Number(i + 1)}</p>
                                    <Badge variant="outline" className="mt-1 text-xs bg-rose-50 text-rose-700 hover:bg-rose-50">
                                      {getBatchName(video?.batchId)}
                                    </Badge>
                                  </div>
                                </div>
                                <Button variant="ghost" onClick={() => { window.open(video.video, '_blank'); }} size="sm" className="text-gray-500 hover:text-gray-700">
                                  <Play className="h-4 w-4" />
                                  <span className="ml-1 hidden sm:inline">Watch</span>
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      <CreateModuleDialog
        open={isCreateModuleOpen || showAddModuleDialog}
        onClose={() => { setIsCreateModuleOpen(false); setShowAddModuleDialog(false) }}
        onSave={handleSaveModule}
        subjectName={getActiveSubject()?.subjectName || ''}
      />
      {/* Add Subject Dialog */}
      <Dialog open={showAddSubjectDialog} onOpenChange={setShowAddSubjectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Subject</DialogTitle>
            <DialogDescription>
              Choose from suggested subjects or create your own to organize your course content
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject Name</Label>
              <SubjectSearchDropdown
                value={newSubjectName}
                allsubjects={allSubjects}
                onChange={setNewSubjectName}
                placeholder="Select or type a new subject..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSubjectDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubject} disabled={!newSubjectName?.trim()}>
              Add Subject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Module</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this draft registration? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteConfirm}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ModulesTab;  